import { useState } from 'react';
import {
  Box,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  HStack,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Select,
  Text,
  Textarea,
  VStack,
  Badge,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import CustomDrawer from 'components/ui/Drawer';
import { useCreateTreatmentPlan } from 'hooks/usePlans';
import { ClinicalEpisode, Patient } from 'types/models';

// ─── Icons ────────────────────────────────────────────────────────────────────

function IconUser() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function IconClipboard() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect width="8" height="4" x="8" y="2" rx="1" ry="1" />
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
    </svg>
  );
}

// ─── Estado config ────────────────────────────────────────────────────────────

const ESTADO_CONFIG: Record<string, { label: string; color: string }> = {
  abierto:        { label: 'Abierto',        color: 'blue'  },
  en_tratamiento: { label: 'En Tratamiento', color: 'green' },
};

const FREQ_LABEL: Record<string, string> = {
  '1': '1 vez por semana',
  '2': '2 veces por semana',
  '3': '3 veces por semana',
  '4': '4 veces por semana',
  '5': '5 veces por semana',
};

// ─── PatientEpisodeCard ───────────────────────────────────────────────────────

interface CardProps {
  patientName: string;
  cedula:      string;
  episodeCode: string;
  motivo:      string;
  diagnostico: string | null;
  estado:      string;
}

function PatientEpisodeCard({ patientName, cedula, episodeCode, motivo, diagnostico, estado }: CardProps) {
  const cardBg       = useColorModeValue('gray.50',  'gray.700');
  const borderColor  = useColorModeValue('gray.200', 'gray.600');
  const labelColor   = useColorModeValue('gray.500', 'gray.400');
  const valueColor   = useColorModeValue('gray.800', 'gray.100');
  const accentBg     = useColorModeValue('indigo.50',  'rgba(99,102,241,0.15)');
  const accentBorder = useColorModeValue('indigo.200', 'indigo.600');
  const accentText   = useColorModeValue('indigo.700', 'indigo.300');
  const purpleBg     = useColorModeValue('purple.50',  'rgba(168,85,247,0.15)');
  const purpleBorder = useColorModeValue('purple.200', 'purple.600');
  const purpleText   = useColorModeValue('purple.600', 'purple.300');

  const estadoInfo = ESTADO_CONFIG[estado] ?? { label: estado, color: 'gray' };

  return (
    <Box bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="10px" overflow="hidden">
      <Box px="16px" py="12px" borderBottom="1px solid" borderColor={borderColor}>
        <HStack spacing="10px">
          <Box w="32px" h="32px" borderRadius="8px" bg={accentBg} border="1px solid" borderColor={accentBorder} display="flex" alignItems="center" justifyContent="center" color={accentText} flexShrink={0}>
            <IconUser />
          </Box>
          <VStack align="flex-start" spacing="0">
            <Text fontSize="sm" fontWeight="700" color={valueColor} lineHeight="1.3">{patientName}</Text>
            <Text fontSize="xs" color={labelColor}>CI: {cedula}</Text>
          </VStack>
        </HStack>
      </Box>
      <Box px="16px" py="12px">
        <HStack spacing="10px" align="flex-start">
          <Box w="32px" h="32px" borderRadius="8px" bg={purpleBg} border="1px solid" borderColor={purpleBorder} display="flex" alignItems="center" justifyContent="center" color={purpleText} flexShrink={0} mt="2px">
            <IconClipboard />
          </Box>
          <VStack align="flex-start" spacing="4px" flex="1">
            <HStack spacing="8px">
              <Text fontSize="xs" fontWeight="600" color={labelColor}>{episodeCode}</Text>
              <Badge colorScheme={estadoInfo.color} fontSize="10px" px="6px" py="1px" borderRadius="4px">{estadoInfo.label}</Badge>
            </HStack>
            <Text fontSize="sm" fontWeight="600" color={valueColor} lineHeight="1.3">{motivo}</Text>
            {diagnostico && <Text fontSize="xs" color={labelColor}>Dx: {diagnostico}</Text>}
          </VStack>
        </HStack>
      </Box>
    </Box>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

interface Props {
  isOpen:   boolean;
  onClose:  () => void;
  episode:  ClinicalEpisode;
  patient:  Patient;
}

export default function TreatmentPlanCreateDrawer({ isOpen, onClose, episode, patient }: Props) {
  const toast      = useToast();
  const createPlan = useCreateTreatmentPlan();

  const sectionLabel = useColorModeValue('gray.500', 'gray.400');

  const [totalSesiones,       setTotalSesiones]       = useState('10');
  const [frecuencia,          setFrecuencia]          = useState('3');
  const [objetivoCortoPlayzo, setObjetivoCortoPlayzo] = useState('');
  const [objetivoLargoPlayzo, setObjetivoLargoPlayzo] = useState('');

  const resetForm = () => {
    setTotalSesiones('10');
    setFrecuencia('3');
    setObjetivoCortoPlayzo('');
    setObjetivoLargoPlayzo('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSave = () => {
    if (!objetivoCortoPlayzo.trim() || !objetivoLargoPlayzo.trim()) {
      toast({
        title: 'Completa los objetivos terapéuticos.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      return;
    }

    createPlan.mutate(
      {
        clinicalEpisodeId:    episode.id,
        totalSessions:        Math.max(1, parseInt(totalSesiones, 10) || 1),
        frequencyDescription: FREQ_LABEL[frecuencia] ?? `${frecuencia} veces por semana`,
        objectives: {
          shortTerm: objetivoCortoPlayzo.trim(),
          longTerm:  objetivoLargoPlayzo.trim(),
        },
      },
      {
        onSuccess: () => {
          toast({
            title: 'Plan de tratamiento creado.',
            status: 'success',
            duration: 2500,
            isClosable: true,
            position: 'top-right',
          });
          handleClose();
        },
        onError: (err: any) => {
          const msg = err?.response?.data?.message ?? 'Error al crear el plan.';
          toast({ title: 'Error', description: msg, status: 'error', duration: 5000, isClosable: true, position: 'top-right' });
        },
      }
    );
  };

  return (
    <CustomDrawer
      isOpen={isOpen}
      onClose={handleClose}
      title={
        <Text fontWeight="700" fontSize="lg">Nuevo Plan de Tratamiento</Text>
      }
      size="lg"
      placement="right"
      width="680px"
      layout="form"
      contentPadding="24px"
      onSave={handleSave}
      isSaving={createPlan.isPending}
      saveButtonText="Crear Plan"
      cancelButtonText="Cancelar"
    >
      {/* Paciente + Episodio */}
      <VStack spacing="10px" align="stretch">
        <HStack spacing="6px">
          <Text fontSize="xs" fontWeight="700" color={sectionLabel} textTransform="uppercase" letterSpacing="0.08em">
            Paciente y Episodio
          </Text>
          <Badge colorScheme="green" fontSize="10px" px="6px" py="1px" borderRadius="4px">
            Auto-seleccionado
          </Badge>
        </HStack>
        <PatientEpisodeCard
          patientName={`${patient.nombres} ${patient.apellidos}`}
          cedula={patient.cedula}
          episodeCode={episode.codigoHc}
          motivo={episode.motivoConsulta}
          diagnostico={episode.diagnosticoPrincipal}
          estado={episode.estado}
        />
      </VStack>

      <Divider />

      {/* Parámetros Generales */}
      <VStack spacing="12px" align="stretch">
        <Text fontSize="xs" fontWeight="700" color={sectionLabel} textTransform="uppercase" letterSpacing="0.08em">
          Parámetros Generales
        </Text>
        <Grid templateColumns="1fr 1fr" gap="12px">
          <GridItem>
            <FormControl>
              <FormLabel fontSize="sm" fontWeight="600" mb="6px">Total Sesiones</FormLabel>
              <NumberInput
                value={totalSesiones}
                min={1}
                max={200}
                onChange={(valStr) => setTotalSesiones(valStr)}
                clampValueOnBlur
              >
                <NumberInputField borderRadius="8px" fontSize="sm" />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl>
              <FormLabel fontSize="sm" fontWeight="600" mb="6px">Frecuencia Sugerida</FormLabel>
              <Select
                value={frecuencia}
                onChange={(e) => setFrecuencia(e.target.value)}
                borderRadius="8px"
                fontSize="sm"
              >
                <option value="1">1 vez / sem.</option>
                <option value="2">2 veces / sem.</option>
                <option value="3">3 veces / sem.</option>
                <option value="4">4 veces / sem.</option>
                <option value="5">5 veces / sem.</option>
              </Select>
            </FormControl>
          </GridItem>
        </Grid>
      </VStack>

      <Divider />

      {/* Objetivos Terapéuticos */}
      <VStack spacing="12px" align="stretch">
        <Text fontSize="xs" fontWeight="700" color={sectionLabel} textTransform="uppercase" letterSpacing="0.08em">
          Objetivos Terapéuticos
        </Text>
        <FormControl isRequired>
          <FormLabel fontSize="sm" fontWeight="600" mb="6px">
            Objetivo a Corto Plazo
            <Text as="span" color="red.400" ml="4px" fontSize="xs" fontWeight="400">(Obligatorio)</Text>
          </FormLabel>
          <Textarea
            placeholder="Ej. Reducir la escala de dolor EVA de 7/10 a 3/10 en las primeras 4 semanas..."
            value={objetivoCortoPlayzo}
            onChange={(e) => setObjetivoCortoPlayzo(e.target.value)}
            borderRadius="8px"
            resize="vertical"
            minH="90px"
            fontSize="sm"
          />
        </FormControl>
        <FormControl isRequired>
          <FormLabel fontSize="sm" fontWeight="600" mb="6px">
            Objetivo a Largo Plazo
            <Text as="span" color="red.400" ml="4px" fontSize="xs" fontWeight="400">(Obligatorio)</Text>
          </FormLabel>
          <Textarea
            placeholder="Ej. Reincorporar al paciente a su rutina laboral y deportiva sin dolor ni limitación funcional..."
            value={objetivoLargoPlayzo}
            onChange={(e) => setObjetivoLargoPlayzo(e.target.value)}
            borderRadius="8px"
            resize="vertical"
            minH="90px"
            fontSize="sm"
          />
        </FormControl>
      </VStack>
    </CustomDrawer>
  );
}
